import cn from 'classnames';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, Checkbox, Prompt } from 'sonata-react-components';

import { getDriveToStoreReportUrl } from '@/components/driveToStore/common/hooks/useDriveToStoreNavigation';

import './selectLineItemPrompt.scss';

export type SelectLineItemPromptProps = {
  entityType: string;
  lineItems: { id: string; name: string; campaignPlanName?: string; ioName?: string }[];
  poiId: string;
  onClose: () => void;
};

type SelectLineItemPromptLineItem = SelectLineItemPromptProps['lineItems'][number];

function getLineItemDescription(
  { campaignPlanName, ioName }: Pick<SelectLineItemPromptLineItem, 'campaignPlanName' | 'ioName'>,
  formatMessage: ReturnType<typeof useIntl>['formatMessage']
): string {
  const notApplicable = formatMessage({
    defaultMessage: 'N/A',
    id: 'driveToStore.aggregatedReport.selectLineItemPrompt.notApplicable'
  });

  return formatMessage(
    {
      defaultMessage: 'Campaign plan: {campaignPlanName} / IO: {ioName}',
      id: 'driveToStore.aggregatedReport.selectLineItemPrompt.description'
    },
    { campaignPlanName: campaignPlanName ?? notApplicable, ioName: ioName ?? notApplicable }
  );
}

export default function SelectLineItemPrompt({
  entityType,
  lineItems,
  poiId,
  onClose
}: Readonly<SelectLineItemPromptProps>) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const { formatMessage } = useIntl();

  const toggleLineItem = (lineItemId: string) => {
    setSelectedIds(prev => (prev.includes(lineItemId) ? prev.filter(id => id !== lineItemId) : [...prev, lineItemId]));
  };

  const handleConfirm = () => {
    selectedIds.forEach(lineItemId => {
      window.open(getDriveToStoreReportUrl(entityType, lineItemId, { poiId }), '_blank', 'noopener,noreferrer');
    });
    onClose();
  };

  return (
    <Prompt
      className="selectLineItemPrompt"
      closePrompt={onClose}
      title={formatMessage({
        defaultMessage: 'Select the Line Item for which you would like to see the single POI report',
        id: 'driveToStore.aggregatedReport.selectLineItemPrompt.title'
      })}
      width="572px"
    >
      <p className="selectLineItemPrompt__counter">
        {formatMessage(
          {
            defaultMessage: '{count, plural, one {# Line Item} other {# Line Items}}',
            id: 'driveToStore.aggregatedReport.selectLineItemPrompt.counter'
          },
          { count: selectedIds.length }
        )}
      </p>
      <div className="selectLineItemPrompt__list">
        {lineItems.map(lineItem => {
          const isSelected = selectedIds.includes(lineItem.id);
          const description = getLineItemDescription(lineItem, formatMessage);

          return (
            <div
              className={cn('selectLineItemPrompt__item', {
                'selectLineItemPrompt__item--selected': isSelected
              })}
              key={lineItem.id}
            >
              <Checkbox
                active={isSelected}
                id={`select-line-item-${lineItem.id}`}
                onClickAction={() => toggleLineItem(lineItem.id)}
                type="checkbox"
              />
              <div className="selectLineItemPrompt__itemContent">
                <span className="selectLineItemPrompt__itemName">{lineItem.name}</span>
                <span className="selectLineItemPrompt__itemDescription">{description}</span>
              </div>
            </div>
          );
        })}
      </div>
      <Prompt.Buttons position="spread">
        <ButtonUI
          id="select-line-item-cancel"
          onClick={onClose}
          shape="squareRounded"
          text={formatMessage({ defaultMessage: 'Cancel', id: 'common.cancel' })}
          type="secondary"
        />
        <ButtonUI
          disabled={selectedIds.length === 0}
          icon="fa-solid fa-arrow-right"
          iconPosition="right"
          id="select-line-item-confirm"
          onClick={handleConfirm}
          shape="squareRounded"
          text={formatMessage({
            defaultMessage: 'Open single report',
            id: 'driveToStore.aggregatedReport.selectLineItemPrompt.confirm'
          })}
          type="primary"
        />
      </Prompt.Buttons>
    </Prompt>
  );
}
